import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpHeaders } from '@angular/common/http';
import {
  PermissionInformation,
  UserDetail,
} from './models/permission-information.model';
import { map } from 'rxjs/operators';
import _get from 'lodash/get';
import _startCase from 'lodash/startCase';

import {
  PermissionType,
  requestCreate,
} from '@app-services/requests/models/permission-type.model';
import { environment } from '@environments/environment';
import { GetListResponse } from '@app-models/general.model';
import {
  cancelResponse,
  PermissionRequestDetail,
} from './models/permission-request.model';
import { HttpService } from '@app-core/services/http/http.service';

@Injectable({
  providedIn: 'root',
})
export class RequestsService {
  API_URL = `${environment.API_DOMAIN}/${environment.API_PATH_PREFIX}`;
  constructor(private httpService: HttpService) {}

  getInformationForNewPermission(): Observable<PermissionInformation> {
    const path = `${this.API_URL}/employee/permission_request/auto_calculate_fields/`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.httpService.get<any>(path, { headers }).pipe(
      map((data) => {
        return {
          requestDate: _get(data, 'date', null),
          employee: _startCase(_get(data, 'employee', null)),
          idNumber: _get(data, 'id_number', null),
          account: _startCase(_get(data, 'account', null)),
          employer: _startCase(_get(data, 'employer', null)),
        };
      })
    );
  }

  getPermissionTypes(): Observable<PermissionType[]> {
    const path = `${this.API_URL}/general/dropdown/?type=permission_request`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.httpService.get<any>(path, { headers }).pipe(
      map((data) => {
        return data.results.map((type) => {
          return {
            id: _get(type, 'id', null),
            name: _get(type, 'name', null),
          };
        });
      })
    );
  }

  public createPermissionRequest(payload: FormData): Observable<requestCreate> {
    const path = `${this.API_URL}/employee/permission_request/`;
    return this.httpService.post<any>(path, payload);
  }

  getRequestPermissionList(payload): Observable<GetListResponse> {
    const path = `${this.API_URL}/employee/permission_request/`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    return this.httpService.get<any>(path, { params: payload, headers }).pipe(
      map((data: any) => {
        return {
          count: _get(data, 'count', 0),
          request: data.results.map((req) => {
            return {
              id: _get(req, 'id', 0),
              account: _get(req, 'customer_name', null),
              description: _get(req, 'description', null),
              employee: _get(req, 'employee_name', null),
              employeer: _get(req, 'employeer_name', null),
              enableCancelRequest: _get(req, 'enable_cancel_request', false),
              enableLegalForm: _get(req, 'enable_legal_form', false),
              endDate: _get(req, 'end_date', null),
              idNumber: _get(req, 'id_number', 0),
              isPdfSigned: _get(req, 'is_document_signed', false),
              isManagerSigned: _get(req, 'is_manager_signed', false),
              permissionReason: _get(req, 'reason_name', null),
              permissionType: _get(req, 'permission_type_name', null),
              requestedDate: _get(req, 'created_at', null),
              startDate: _get(req, 'start_date', null),
              supportDocument: _get(req, 'support_document', null),
              timeLeft: _get(req, 'duration', null),
              timeline: _get(req, 'timeline', []),
              currentStatus: _get(
                req.timeline[req.timeline.length - 1],
                'status__name',
                ''
              ),
              hasSupportUpload: _get(req, 'has_support_upload', null),
              document: _get(req, 'full_document', null),
              iframeIdEmployee: _get(req, 'iframe_id_employee', null),
            };
          }),
        };
      })
    );
  }

  public cancelPermissionRequest(id: number): Observable<cancelResponse> {
    const path = `${this.API_URL}/employee/permission_request/cancel_permission/`;
    return this.httpService.put<any>(path, { id: id, status: 'cancelled' });
  }
  public perfil(id: number): Observable<UserDetail> {
    const path = `${this.API_URL}/employee/new/profile/` + id + '/';
    return this.httpService.get<any>(path).pipe(
      map((data: any) => {
        return {
          name:
            _get(data, 'detailperson.name', null) +
            ' ' +
            _get(data, 'detailperson.last_name', null),
          document:
            _get(data, 'detailperson.identity_document_type_name', null) +
            _get(data, 'detailperson.identity_document', null),
        };
      })
    );
  }

  getInformationForPermission(id: number): Observable<PermissionRequestDetail> {
    const path = `${this.API_URL}/employee/permission_request/` + id + '/';
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });
    return this.httpService.get<any>(path, { headers }).pipe(
      map((data) => {
        return {
          reason: _get(data, 'reason_name', null),
          startDate: _get(data, 'start_date', null),
          endDate: _get(data, 'end_date', null),
          description: _get(data, 'description', ''),
          supportDocument: _get(data, 'support_document', null),
        };
      })
    );
  }

  public updatePermissionRequest(
    id: number,
    payload: FormData
  ): Observable<requestCreate> {
    const path = `${this.API_URL}/employee/permission_request/` + id + '/';
    return this.httpService.put<any>(path, payload);
  }

  public downloadSignedDocument(id: number) {
    const path =
      `${this.API_URL}/employee/permission_request/` +
      id +
      '/download_document/';
    window.open(path, '_blank');
  }
}
